import { useState, useEffect } from 'react'

// Breakpoints are the upper limit of the screen widths that correspond to that size
// e.g. the “medium” screen size ends at 960px (exclusive)
export const breakpoints = {
  extraSmall: '320px',
  small: '600px',
  medium: '960px',
  large: '1280px',
  extraLarge: '1920px',
}

export const breakpointValues = Object.entries(breakpoints).reduce<
  Record<keyof typeof breakpoints, number>
>(
  (values, [key, str]) => ({
    ...values,
    [key]: parseInt(str.replace('px', '')),
  }),
  {} as Record<keyof typeof breakpoints, number>
)

// These helper queries can be passed directly to useMediaQuery
// or interpolated into a CSS media query like `@media (${mediaQueries.small.up})`
export const mediaQueries = {
  small: {
    up: `screen and (width >= ${breakpoints.extraSmall})`,
    down: `screen and (width < ${breakpoints.small})`,
    only: `screen and (${breakpoints.extraSmall} <= width < ${breakpoints.small})`,
  },
  medium: {
    up: `screen and (width >= ${breakpoints.small})`,
    down: `screen and (width < ${breakpoints.medium})`,
    only: `screen and (${breakpoints.small} <= width < ${breakpoints.medium})`,
  },
  large: {
    up: `screen and (width >= ${breakpoints.medium})`,
    down: `screen and (width < ${breakpoints.large})`,
    only: `screen and (${breakpoints.medium} <= width < ${breakpoints.large})`,
  },
  extraLarge: {
    up: `screen and (width >= ${breakpoints.large})`,
    down: `screen and (width < ${breakpoints.extraLarge})`,
    only: `screen and (${breakpoints.large} <= width < ${breakpoints.extraLarge})`,
  },
}

export function useMediaQuery(query: string): boolean {
  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  }

  const [matches, setMatches] = useState<boolean>(getMatches(query))

  function handleChange() {
    setMatches(getMatches(query))
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query)

    // Triggered at the first client-side load and if query changes
    handleChange()

    // Listen matchMedia
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener('change', handleChange)
    } else {
      matchMedia.addEventListener('change', handleChange)
    }

    return () => {
      if (matchMedia.removeEventListener) {
        matchMedia.removeEventListener('change', handleChange)
      } else {
        matchMedia.removeEventListener('change', handleChange)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return matches
}
